import { FC, useMemo } from "react";

import { FormBackgroundTheme } from "@simplyk/common";

import type { CanvasAnimationLayoutVariant } from "./canvasBackgroundTheme";
import { CardsDiceAnimation } from "./CardsDiceAnimation";
import { ConnectorsAnimation } from "./ConnectorsAnimation";
import { FallingLeavesAnimation } from "./FallingLeavesAnimation";
import { HeartsAnimation } from "./HeartsAnimation";
import { hexToHue } from "./hexToHue";
import { mapFormThemeToCanvasBackgroundTheme } from "./mapFormThemeToCanvasBackgroundTheme";
import { MusicNotesAnimation } from "./MusicNotesAnimation";
import { PrideFlagsAnimation } from "./PrideFlagsAnimation";
import { RippleHeartsAnimation } from "./RippleHeartsAnimation";
import { RisingLightsAnimation } from "./RisingLightsAnimation";
import { SnowflakeAnimation } from "./SnowflakeAnimation";
import { SparklesAnimation } from "./SparklesAnimation";
import { SportsBallsAnimation } from "./SportsBallsAnimation";

import { isLowEndDevice, readDeviceCapabilities } from "@/common/helpers/deviceCapabilities";
import { useDynamicThemeAwareVariables } from "@/common/hooks/useDynamicThemeAwareVariables";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";

const LOW_END_FRAME_STRIDE = 2;

export type FormCanvasDonationBackgroundProps = {
  kind: FormBackgroundTheme;
  brandColorHex: string;
  canvasVariant?: CanvasAnimationLayoutVariant;
};

export const FormCanvasDonationBackground: FC<FormCanvasDonationBackgroundProps> = ({
  kind,
  brandColorHex,
  canvasVariant = "layerFill",
}) => {
  const canvasTheme = useDynamicThemeAwareVariables(mapFormThemeToCanvasBackgroundTheme);
  const hue = useMemo(() => hexToHue(brandColorHex), [brandColorHex]);
  const { isSmallScreen } = useMediaQuery(undefined, { noSsr: true });
  const frameStride = isLowEndDevice(readDeviceCapabilities()) ? LOW_END_FRAME_STRIDE : 1;

  const animationProps = { hue, theme: canvasTheme, variant: canvasVariant, isMobile: isSmallScreen, frameStride };

  switch (kind) {
    case FormBackgroundTheme.Sparkles:
      return <SparklesAnimation {...animationProps} />;
    case FormBackgroundTheme.Hearts:
      return <HeartsAnimation {...animationProps} />;
    case FormBackgroundTheme.Connectors:
      return <ConnectorsAnimation {...animationProps} />;
    case FormBackgroundTheme.Music:
      return <MusicNotesAnimation {...animationProps} />;
    case FormBackgroundTheme.Cards:
      return <CardsDiceAnimation {...animationProps} />;
    case FormBackgroundTheme.Faith:
      return <RisingLightsAnimation {...animationProps} />;
    case FormBackgroundTheme.Sports:
      return <SportsBallsAnimation {...animationProps} />;
    case FormBackgroundTheme.Fall:
      return <FallingLeavesAnimation {...animationProps} />;
    case FormBackgroundTheme.Winter:
      return <SnowflakeAnimation {...animationProps} />;
    case FormBackgroundTheme.GivingTuesday:
      return <RippleHeartsAnimation hue={hue} theme={canvasTheme} variant={canvasVariant} frameStride={frameStride} />;
    case FormBackgroundTheme.Pride:
      return <PrideFlagsAnimation variant={canvasVariant} isMobile={isSmallScreen} frameStride={frameStride} />;
    default:
      return null;
  }
};
